import { Providers } from "./lib/providers";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { privateRoutes, routes } from "./pages";
import "./i18n";
import "bootstrap/dist/css/bootstrap.min.css";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  checkBitanicaLinkApi,
  fetchUserIp,
  getMaintenance,
  getSettings,
  telegramBotAutoLogin,
  telegramLogin,
  userSession,
} from "./lib/store/actions";
import PrivateRoute from "./components/PrivateRoute";
import "./App.css";
import MainLayout from "./components/Layout/Mainlayout";
import { useNavigate } from "react-router-dom";
import { ToastProvider } from "./context/CustomToast";
import GameEnterRoute from "./components/GameEnterRoute";
import { useIsTelegramWebApp } from "./hooks/useTelegram";
import { GameProvider, useGameContext } from "./context/GameContext";
import { decrypt } from "./helper/secretManager";
import Maintenance from "./pages/Maintenance";
import { isMobile, encryptData, sanitizeForEncryption } from "./helper/common";
import { authSlice } from "./lib/store/auth/slice";
import useSocket from "./hooks/useSocket";
import { useWindow } from "./hooks/useWindow";
import { lazy, Suspense } from "react";
import AuthLayout from "./components/Layout/Authlayout";

const TonConnectProvider = lazy(
  () => import("../src/pages/MainPages/wallet/Component/TonConnectProvider"),
);

function App() {
  console.log = function () { };

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <TonConnectProvider>
        <ToastProvider>
          <Providers>
            <ToastContainer
              // transition={"Slide"}
              position="top-center"
              hideProgressBar={true}
              closeButton={false}
              limit={1}
              pauseOnFocusLoss={false}
              pauseOnHover={false}
            // autoClose={150000}
            />
            <GameProvider>
              <BrowserRouter>
                <RoutesList />
              </BrowserRouter>
            </GameProvider>
          </Providers>
        </ToastProvider>
      </TonConnectProvider>
    </Suspense>
  );
}

const RoutesList = () => {
  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  //******************** */
  const timeoutRef = useRef(null); // Use ref to store timeout
  // const timeoutDone = useRef(false);
  //******************** */

  // useEffect(() => {
  //   // Set a timeout to trigger after 500ms
  //   timeoutRef.current = setTimeout(() => {
  //     timeoutDone.current = true; // Mark timeout as done
  //   }, 500);

  //   // Clean up timeout when component unmounts
  //   return () => clearTimeout(timeoutRef.current);
  // }, []);

  const { user, isBitanicaLinked } = useSelector(({ Auth }) => ({
    user: Auth.user,
  }));

  // const { tasksList } = useSelector(({ Tasks }) => ({
  //   tasksList: Tasks.tasksList,
  // }));
  // const {
  //   screenSize: { dynamicHeight },
  // } = useWindow();
  const [sessionLoader, setSessionLoader] = useState(false);
  const [loginLoader, setLoginLoader] = useState(true);
  const { setGame, setGameStart, setGameLoading, gameLoading } =
    useGameContext();
  const maxRetries = 5; // Maximum number of retries
  const retryCountRef = useRef(0);

  const { socketConnect } = useSocket();
  const isTelegramWebApp = useIsTelegramWebApp();
  const userId = user?._id;
  const userStatus = user?.status;

  const ativeGameDetail = useCallback(async (userData) => {
    return new Promise(async (resolve, reject) => {
      try {
        if (
          (userData?._id && !userData?.currentGame?._id) ||
          !userData?._id ||
          !userData?.currentGame?._id
        ) {
          setTimeout(() => {
            setGameLoading(false);
            resolve();
          }, 200);
          return;
        }

        setGameLoading(true);
        if (userData?.currentGame?._id) {
          let updatedData = {
            ...userData?.currentGame,
            gameData: [],
            lineData: [],
          };

          updatedData = {
            ...updatedData,
            gameData: userData?.currentGame?.gameData,
            lineData: userData?.currentGame?.lineData,
            winnings: updatedData?.winnings?.length
              ? [...updatedData.winnings]
              : [],
          };

          setGame({
            ...updatedData,
            gameData: updatedData?.gameData?.reverse(),
            lineData: updatedData?.lineData?.reverse(),
          });
          resolve(updatedData);
          setGameLoading(false);
          setGameStart(true);
        } else {
          setGameLoading(false);
          resolve(); // Resolve if no game data
        }
      } catch (err) {
        setGameLoading(false);
        reject(err); // Reject if there is an exception
      }
    });
  }, [setGame, setGameStart, setGameLoading]);

  const telegramWebAppLogin = useCallback(() => {
    if (retryCountRef.current > maxRetries) {
      setTimeout(() => {
        setLoginLoader(false);
      }, 6000);
      return;
    }

    try {
      if (
        isTelegramWebApp &&
        window.Telegram.WebApp.initDataUnsafe?.user &&
        isMobile()
      ) {
        // const startParam = window.Telegram.webApp?.initDataUnsafe?.start_parameter;
        // if (startParam && startParam?.startsWith('payment_')) {
        //   const orderID = startParam?.replace('payment_', '');
        //   if (orderID) {
        //     navigate(`/payments?order_id=${orderID}`);
        //   }
        // }
        const telegramUser = window.Telegram.WebApp.initDataUnsafe.user;
        setLoginLoader(true); // Show loader while trying to log in
        dispatch(authSlice.actions.setLoginBotQrLoader({ type: "botLogin" }));
        let loginObj = {};
        if (telegramUser?.id)
          loginObj.telegram_id = telegramUser.id?.toString();
        if (telegramUser?.username)
          loginObj.telegram_username = telegramUser.username;
        if (telegramUser?.first_name)
          loginObj.first_name = telegramUser.first_name;
        if (telegramUser?.last_name)
          loginObj.last_name = telegramUser.last_name;
        const encryptedPayload = encryptData(loginObj);
        dispatch(
          telegramBotAutoLogin({
            data: { payload: encryptedPayload },
            callBack: async (err, data) => {
              if (parseInt(err?.status_code) === 406) {
                setLoginLoader(false);
                return navigate("/maintenance");
              }
              if (err) return; //if login error then exit retrying
              // setTimeout(() => {
              setLoginLoader(false);
              // }, 4000)
              await ativeGameDetail(data);
              navigate("/game");
            },
          }),
        );
      } else {
        // No user data received, retry login
        retryCountRef.current += 1;
        setTimeout(() => {
          telegramWebAppLogin(); // Retry
        }, 1000); // Retry after 2 seconds
      }
    } catch (err) {
      console.error("Error during Telegram WebApp login", err);
    }
  }, [isTelegramWebApp, dispatch, navigate, ativeGameDetail]);

  const checkAuth = useCallback(async () => {
    try {
      setSessionLoader(true);

      await dispatch(
        userSession({
          callBack: async (err, userData) => {
            if (err) {
              setSessionLoader(false);

              if (
                !["/login", "/", "/success"].includes(window.location.pathname)
              ) {
                document.cookie = "";
                navigate("/login");
              }

              return;
            }

            if (userData?.status === "active") {
              await ativeGameDetail(userData);

              setGameLoading(false);
              setSessionLoader(false);

              navigate("/game");
            } else if (userData?.status === "pending") {
              setSessionLoader(false);

              navigate("/verify-otp", {
                state: { email: userData?.email },
              });
            } else {
              setSessionLoader(false);
            }
          },
        }),
      );
    } catch (error) {
      console.error(error);

      setSessionLoader(false);
      setGameLoading(false);
    }
  }, [dispatch, navigate, ativeGameDetail, setGameLoading, setSessionLoader]);

  useEffect(() => {
    dispatch(
      fetchUserIp({
        callBack: (err, data) => {
          if (err) {
            console.error("Error fetching IP:", err);
          } else {
          }
        },
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    setLoginLoader(true);

    dispatch(
      getMaintenance({
        callBack: async (err, data) => {
          if (err) {
            setLoginLoader(false);
            return;
          }

          const telegramUser = window.Telegram?.WebApp?.initDataUnsafe?.user;
          const currentPath = window.location.pathname;

          // Maintenance ON
          if (data?.status) {
            setTimeout(() => {
              setLoginLoader(false);
            }, 2000);

            if (currentPath !== "/maintenance") {
              navigate("/maintenance");
            }

            return;
          }

          // IMPORTANT
          setLoginLoader(false);

          if (currentPath === "/maintenance") {
            navigate("/login");
          }

          if (telegramUser && isMobile()) {
            telegramWebAppLogin();
          } else {
            checkAuth();
          }
        },
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isTelegramWebApp && window.Telegram?.WebApp) {
      const tele = window.Telegram.WebApp; // Initialize the TELEGRAM BOT
      tele.expand(); // Expands the app on the users' phone to 100% height
    }
  }, [isTelegramWebApp]);

  useEffect(() => {
    if (!socketConnect) return;

    const telegramUser = window.Telegram?.WebApp?.initDataUnsafe?.user;
    const hash = window.location.hash;

    if (!userId && !isTelegramWebApp && !telegramUser && !hash) {
      checkAuth();
    } else {
    }
  }, [socketConnect, userId, isTelegramWebApp, checkAuth]);

  useEffect(() => {
    const preventScroll = (event) => {
      const target = event.target;

      const isSlider = target.closest(".rangeSlider");
      const isInTonConnect = target.closest("#tc-widget-root");
      const isScrollable = target.closest(".allow-scroll");
      const noMini = target.closest(".no-minimize");

      // Prevent scroll if NOT in TonConnect OR if .no-minimize is present
      if ((!isInTonConnect && !isScrollable && !isSlider) || noMini) {
        event.preventDefault();
      }
    };

    const timeout = setTimeout(() => {
      document.body.style.overflow = "hidden";

      document.addEventListener("touchmove", preventScroll, { passive: false });
    }, 100);

    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("touchmove", preventScroll);
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    if (pathname.includes("/login")) {
      // Get the hash part of the URL
      const hash = window.location.hash;

      // Extract the tgAuthResult if it's present in the hash
      const match = hash.match(/tgAuthResult=([^&]*)/);
      if (match && match[1]) {
        // URL decode the value (optional, depending on the encoding)
        const token = decodeURIComponent(match[1]);

        const payload = {
          token: token,
        };
        if (payload.token) {
          dispatch(
            telegramLogin({
              data: payload,
              callBack: async (err, data) => {
                if (parseInt(err?.status_code) === 406) {
                  return navigate("/maintenance");
                }
                if (err) {
                  return;
                }
                if (data) {
                  await ativeGameDetail(data);
                  setTimeout(() => {
                    navigate("/game");
                  }, 3000);
                }
              },
            }),
          );
        }
      }
    }
  }, [pathname, dispatch, navigate, ativeGameDetail]);

  return (
    <Routes>
      <Route
        exact={true}
        path={"/maintenance"}
        element={
          <Maintenance
            sessionLoader={sessionLoader}
            loginLoader={loginLoader}
            gameLoader={gameLoading}
          />
        }
      ></Route>
      <Route element={<AuthLayout />}>
        <Route index element={<Navigate to="/login" />} />
        {routes.map((data, index) => (
          <Route
            exact={true}
            path={data.path}
            element={
              <GameEnterRoute
                // timeoutDone={timeoutDone.current}
                sessionLoader={sessionLoader}
                loginLoader={loginLoader}
                gameLoader={gameLoading}
              >
                {data.component}
              </GameEnterRoute>
            }
            key={index}
          />
        ))}
      </Route>
      <Route element={<MainLayout />}>
        <Route index element={<Navigate to="/game" />} />
        {privateRoutes.map((data, index) => (
          <Route
            exact={true}
            path={data.path}
            element={
              <PrivateRoute
                // timeoutDone={timeoutDone.current}
                sessionLoader={sessionLoader}
                loginLoader={loginLoader}
                gameLoader={gameLoading}
              >
                {data.component}
              </PrivateRoute>
            }
            key={index}
          />
        ))}
      </Route>
    </Routes>
  );
};

export default App;
